import cuid from "cuid";
import moment from "moment";
import { createReducer } from "typesafe-actions";
import withJobs from "../../_core/job/reducers";
import {
	EGarbageRecordState,
	IGarbageRecordNewOperationState,
	IGarbageRecordStore,
} from "./@types";
import garbageRecordActions, {
	garbageRecordConstants,
	TGarbageRecordActions,
} from "./actions";
import garbageRecordUtils from "./utils";

const defaultNewOperationState: IGarbageRecordNewOperationState =
	garbageRecordUtils.getDefaultOperationData();

const defaultState: IGarbageRecordStore = {
	records: {
		byIds: {},
		ids: [],
	},
	newOperation: defaultNewOperationState,
	filter: {
		startDate: moment().subtract(7, "days").toDate(),
		endDate: moment().add(1, "day").toDate(),
		shownRecords: "ALL",
	},
	garbageQuestionsState: "NOT-REQUIRED",
	garbageDischargedIntoSeaQuestions: [],
	_forms: {},
	_jobs: {},
};
const garbageRecordReducers = createReducer<
	IGarbageRecordStore,
	TGarbageRecordActions
>(defaultState)
	.handleAction(
		garbageRecordActions.document.basicFormEdit,
		function basicFormEdit(state, action) {
			return {
				...state,
				newOperation: {
					...state.newOperation,
					garbageBasicInfo: {
						...state.newOperation.garbageBasicInfo,
						...action.payload.basicGarbageForm,
					},
				},
			};
		}
	)
	.handleAction(
		garbageRecordActions.document.dischargeEntryFormEdit,
		function dischargeEntryFormEdit(state, action) {
			const newDataForEntry = action.payload.dischargeEntryBasedForm;
			switch (newDataForEntry.entry) {
				case "GARBAGE_INCINERATED":
					return {
						...state,
						newOperation: {
							...state.newOperation,
							garbageIncinerated: {
								...state.newOperation.garbageIncinerated,
								...newDataForEntry,
							},
						},
					};
				case "GARBAGE_DISCHARGED_INTO_SEA":
					return {
						...state,
						newOperation: {
							...state.newOperation,
							garbageDischargedIntoSea: {
								...state.newOperation.garbageDischargedIntoSea,
								...newDataForEntry,
							},
						},
					};
				case "ACCIDENTAL_OR_LOSS_OF_GARBAGE_INTO_SEA":
					return {
						...state,
						newOperation: {
							...state.newOperation,
							garbageAccidentalLoss: {
								...state.newOperation.garbageAccidentalLoss,
								...newDataForEntry,
							},
						},
					};
				case "GARBAGE_DISCHARGED_TO_RECEPTION_ASHORE_OR_OTHER_SHIPS":
					return {
						...state,
						newOperation: {
							...state.newOperation,
							garbageDischargedToReceptionAshoreOrOtherShips: {
								...state.newOperation
									.garbageDischargedToReceptionAshoreOrOtherShips,
								...newDataForEntry,
							},
						},
					};
			}
		}
	)
	.handleAction(
		garbageRecordActions.document.newOperationStateSet,
		function newOperationStateSet(state, action) {
			return {
				...state,
				newOperation: {
					...state.newOperation,
					newOperationState: action.payload.state,
				},
			};
		}
	)
	.handleAction(
		garbageRecordActions.document.formReset,
		function formReset(state) {
			return {
				...state,
				newOperation: defaultNewOperationState,
			};
		}
	)
	.handleAction(
		garbageRecordActions.document.historySet,
		function historySet(state, action) {
			return {
				...state,
				records: action.payload.history,
			};
		}
	)
	.handleAction(
		garbageRecordActions.document.garbageRecordStateSet,
		function garbageRecordStateSet(state, action) {
			const { state: garbageRecordState, garbageRecordId } = action.payload;
			return {
				...state,
				records: {
					...state.records,
					byIds: {
						...state.records.byIds,
						[garbageRecordId]: {
							...state.records.byIds[garbageRecordId],
							state: garbageRecordState,
						},
					},
				},
			};
		}
	)
	.handleAction(
		garbageRecordActions.document.editLogsEdit,
		function editLogsEdit(state, action) {
			const { editSession, edits } = action.payload;
			if (editSession === 0) return state;
			const editsForGivenSession = state.newOperation.garbageBasicInfo.editLogs[
				editSession
			]
				? state.newOperation.garbageBasicInfo.editLogs[editSession]
				: {
						byIds: {},
						ids: [],
				  };
			const newEdits = {
				...editsForGivenSession,
				byIds: {
					...editsForGivenSession.byIds,
					[edits.fieldName]: edits,
				},
			};
			newEdits.ids = Object.keys(newEdits.byIds);
			return {
				...state,
				newOperation: {
					...state.newOperation,
					garbageBasicInfo: {
						...state.newOperation.garbageBasicInfo,
						editLogs: {
							...state.newOperation.garbageBasicInfo.editLogs,
							[editSession]: {
								...newEdits,
							},
						},
					},
				},
			};
		}
	)
	.handleAction(
		garbageRecordActions.document.newOperationSet,
		function newOperationSet(state, action) {
			const { garbageRecord } = action.payload;
			return {
				...state,
				newOperation: garbageRecordUtils.getOperationToSubmit(garbageRecord),
			};
		}
	)
	.handleAction(
		garbageRecordActions.document.newOperationSetForEdit,
		function newOperationSetForEdit(state, action) {
			const { garbageRecord } = action.payload;
			return {
				...state,
				newOperation: garbageRecordUtils.getOperationForEdit(garbageRecord),
			};
		}
	)
	.handleAction(
		garbageRecordActions.document.filterSet,
		function filterSet(state, action) {
			return {
				...state,
				filter: action.payload.filter,
			};
		}
	)
	.handleAction(
		garbageRecordActions.document.garbageDischargedIntoSeaQuestionStateSet,
		function garbageDischargedIntoSeaQuestionStateSet(state, { payload }) {
			const { dischargedIntoSeaQuestionState } = payload;
			if (dischargedIntoSeaQuestionState === "NOT-REQUIRED")
				return {
					...state,
					garbageQuestionsState: payload.dischargedIntoSeaQuestionState,
					garbageDischargedIntoSeaQuestions: [],
				};
			return {
				...state,
				garbageQuestionsState: payload.dischargedIntoSeaQuestionState,
			};
		}
	)
	.handleAction(
		garbageRecordActions.document.garbageDischargedIntoSeaQuestionSet,
		function garbageDischargedIntoSeaQuestionSet(state, action) {
			return {
				...state,
				garbageDischargedIntoSeaQuestions: action.payload.questions,
			};
		}
	);
export default withJobs<IGarbageRecordStore>(
	garbageRecordConstants.FEATURE,
	defaultState,
	garbageRecordReducers
);
